/**
 * components/Onboarding/services/onboardingDraftUtils.ts
 * Runtime utilities for OnboardingDraft
 *
 * Contains factory functions, validators, and helpers for OnboardingDraft.
 * Separated from type definitions per CODING_STANDARDS (src/types/ is type-only).
 */

// Type imports
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import type { OnboardingScreenId } from '@/components/Onboarding/types/screens';
import type { AccountType } from '@/types/shared/AccountType';

// Runtime imports
import { isValidAccountType, isPersonalLikeAccountType } from '@/utils/accountType';

/**
 * Default onboarding draft with sensible defaults
 * Uses auto-detected timezone and common trading preferences
 */
export function createDefaultOnboardingDraft(): OnboardingDraft {
  const now = new Date().toISOString();

  return {
    createdAt: now,
    lastModified: now,
    currentScreen: 'welcome',
    completedScreens: [],

    personal: {
      // Empty initially - will be filled out when user reaches basic settings screen
      displayTimezone: '',
      timeFormat: '24h' as const,
      weekStartsOn: 'monday' as const,
      tradingSessions: []
    },
    account: {
      accountName: '',
      accountDescription: '',
      accountType: 'personal' // Default to personal account
    },
    trading: {
      // Placeholder - will be filled out in trading setup screen
    }
  };
}

// Note: Browser timezone detection removed - timezone will be set during personal setup

/**
 * Type guard to check if onboarding draft is valid
 */
export function isValidOnboardingDraft(draft: unknown): draft is OnboardingDraft {
  if (!draft || typeof draft !== 'object') return false;

  const obj = draft as Record<string, unknown>;

  // Check basic required fields
  if (typeof obj['createdAt'] !== 'string' ||
      typeof obj['lastModified'] !== 'string' ||
      typeof obj['currentScreen'] !== 'string' ||
      !Array.isArray(obj['completedScreens'])) {
    return false;
  }

  // Check personal section
  const personal = obj['personal'] as Record<string, unknown>;
  if (!personal || typeof personal !== 'object' ||
      typeof personal['displayTimezone'] !== 'string' ||
      !['12h', '24h'].includes(personal['timeFormat'] as string) ||
      !['sunday', 'monday'].includes(personal['weekStartsOn'] as string) ||
      !Array.isArray(personal['tradingSessions'])) {
    return false;
  }

  // Check account section
  const account = obj['account'] as Record<string, unknown>;
  if (!account || typeof account !== 'object' ||
      typeof account['accountName'] !== 'string' ||
      !isValidAccountType(account['accountType'])) {
    return false;
  }

  const accountType = account['accountType'] as AccountType;

  // Check custom account type name if "other" is selected
  if (accountType === 'other' &&
      account['customAccountTypeName'] !== undefined &&
      typeof account['customAccountTypeName'] !== 'string') {
    return false;
  }

  // Validate based on account type - during onboarding, optional fields are allowed
  if (isPersonalLikeAccountType(accountType) || accountType === 'paper') {
    return !account['selectedAssetClasses'] || Array.isArray(account['selectedAssetClasses']);
  }

  // For prop accounts during onboarding, propFirmDetails can be missing or incomplete
  return accountType === 'prop';
}

/**
 * Calculate onboarding progress based on completed fields
 */
export function calculateOnboardingProgress(draft: OnboardingDraft): number {
  let totalFields = 0;
  let completedFields = 0;

  // Personal screen (3 required fields + 1 optional)
  totalFields += 3;
  if (draft.personal.displayTimezone) completedFields++;
  if (draft.personal.timeFormat) completedFields++;
  if (draft.personal.weekStartsOn) completedFields++;
  // tradingSessions is optional - don't count toward progress

  // Account screen (base fields: 2 required + 1 optional)
  totalFields += 3;
  if (draft.account.accountName.trim()) completedFields++; // Required field
  if (draft.account.accountDescription !== undefined) completedFields++; // Optional but counts if provided
  if (draft.account.accountType) completedFields++; // Always has default

  // Account screen (selected asset classes if applicable)
  if (['personal', 'paper'].includes(draft.account.accountType)) {
    totalFields += 1; // selectedAssetClasses
    if (draft.account.selectedAssetClasses && draft.account.selectedAssetClasses.length > 0) completedFields++;
  }

  // Account screen (prop firm fields if applicable)
  if (draft.account.accountType === 'prop') {
    totalFields += 2; // firmName, accountStage
    if (draft.account.propFirmDetails?.firmName) completedFields++;
    if (draft.account.propFirmDetails?.accountStage) completedFields++;
  }

  // TODO: Add other screens when they're built
  // Trading screen
  // Workflow screen
  // Completion screen

  return Math.round((completedFields / totalFields) * 100);
}

/**
 * Helper to update draft with new screen completion
 */
export function markScreenCompleted(
  draft: OnboardingDraft,
  screen: OnboardingScreenId
): OnboardingDraft {
  const completedScreens = [...draft.completedScreens];
  if (!completedScreens.includes(screen)) {
    completedScreens.push(screen);
  }

  return {
    ...draft,
    lastModified: new Date().toISOString(),
    completedScreens
  };
}
